import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Plus, Trash2, Upload, Users, Edit3, X, ClipboardPaste, Download, RefreshCw, Tag, CheckSquare, Square } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({
  'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
  'Content-Type': 'application/json',
});

export default function EmailContactLists() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [selectedList, setSelectedList] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [showImport, setShowImport] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editing, setEditing] = useState(null); // list being renamed
  const [tags, setTags] = useState([]); // available tags for the selected list
  const [tagFilter, setTagFilter] = useState([]); // active tag chips filtering the contact table
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [bulkTagInput, setBulkTagInput] = useState('');
  const [editingTagFor, setEditingTagFor] = useState(null); // contact.id being tagged
  const [tagInput, setTagInput] = useState('');
  const csvRef = useRef(null);

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/email/lists`, { headers: auth() });
      if (r.ok) setLists(await r.json());
      else toast.error('Failed to load lists');
    } catch {
      toast.error('Failed to load lists');
    }
    setLoading(false);
  }, []);

  const fetchContacts = useCallback(async (listId) => {
    try {
      const [rc, rt] = await Promise.all([
        fetch(`${API}/api/email/lists/${listId}/contacts?limit=500`, { headers: auth() }),
        fetch(`${API}/api/email/lists/${listId}/tags`, { headers: auth() }),
      ]);
      if (rc.ok) {
        const data = await rc.json();
        setContacts(data.contacts || []);
        setTotalContacts(data.total || 0);
      }
      if (rt.ok) setTags(await rt.json());
    } catch {
      toast.error('Failed to load contacts');
    }
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);
  useEffect(() => {
    setTagFilter([]); setSelectedContactIds([]); setEditingTagFor(null);
    if (selectedList) fetchContacts(selectedList.id);
    else { setContacts([]); setTags([]); }
  }, [selectedList, fetchContacts]);

  const visibleContacts = useMemo(() => {
    if (!tagFilter.length) return contacts;
    const keys = tagFilter.map(t => t.toLowerCase());
    return contacts.filter(c => {
      const ct = (c.tags || []).map(t => t.toLowerCase());
      return keys.every(k => ct.includes(k));
    });
  }, [contacts, tagFilter]);

  const toggleContact = (id) => {
    setSelectedContactIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAllVisible = () => {
    const ids = visibleContacts.map(c => c.id);
    const allSelected = ids.every(id => selectedContactIds.includes(id));
    setSelectedContactIds(allSelected ? [] : ids);
  };

  const addTagToContact = async (contact, tag) => {
    const cleaned = (tag || '').trim();
    if (!cleaned) return;
    const existing = contact.tags || [];
    if (existing.some(t => t.toLowerCase() === cleaned.toLowerCase())) {
      setEditingTagFor(null); setTagInput(''); return;
    }
    try {
      const r = await fetch(`${API}/api/email/lists/${selectedList.id}/contacts/${contact.id}`, {
        method: 'PATCH', headers: auth(),
        body: JSON.stringify({ tags: [...existing, cleaned] }),
      });
      if (r.ok) { setEditingTagFor(null); setTagInput(''); fetchContacts(selectedList.id); }
    } catch { toast.error('Tag update failed'); }
  };

  const removeTagFromContact = async (contact, tag) => {
    const next = (contact.tags || []).filter(t => t.toLowerCase() !== tag.toLowerCase());
    try {
      const r = await fetch(`${API}/api/email/lists/${selectedList.id}/contacts/${contact.id}`, {
        method: 'PATCH', headers: auth(),
        body: JSON.stringify({ tags: next }),
      });
      if (r.ok) fetchContacts(selectedList.id);
    } catch { toast.error('Tag update failed'); }
  };

  const bulkAddTags = async () => {
    const raw = bulkTagInput.split(',').map(t => t.trim()).filter(Boolean);
    if (!raw.length || !selectedContactIds.length) return;
    try {
      const r = await fetch(`${API}/api/email/lists/${selectedList.id}/tags/bulk`, {
        method: 'POST', headers: auth(),
        body: JSON.stringify({ contact_ids: selectedContactIds, add: raw }),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Tagged ${data.modified} contact(s)`);
        setBulkTagInput('');
        fetchContacts(selectedList.id);
      } else { toast.error('Bulk tag failed'); }
    } catch { toast.error('Bulk tag failed'); }
  };
  const bulkRemoveTag = async (tag) => {
    if (!selectedContactIds.length) return;
    try {
      const r = await fetch(`${API}/api/email/lists/${selectedList.id}/tags/bulk`, {
        method: 'POST', headers: auth(),
        body: JSON.stringify({ contact_ids: selectedContactIds, remove: [tag] }),
      });
      if (r.ok) { toast.success('Tag removed'); fetchContacts(selectedList.id); }
    } catch { toast.error('Bulk untag failed'); }
  };

  const createList = async () => {
    if (!newName.trim()) return toast.error('Name required');
    try {
      const r = await fetch(`${API}/api/email/lists`, {
        method: 'POST', headers: auth(),
        body: JSON.stringify({ name: newName.trim(), description: newDesc.trim() }),
      });
      if (r.ok) {
        const created = await r.json();
        toast.success('List created');
        setNewName(''); setNewDesc(''); setCreating(false);
        await fetchLists();
        setSelectedList(created);
      } else {
        toast.error('Create failed');
      }
    } catch { toast.error('Create failed'); }
  };

  const saveEdit = async () => {
    if (!editing?.name?.trim()) return toast.error('Name required');
    try {
      const r = await fetch(`${API}/api/email/lists/${editing.id}`, {
        method: 'PUT', headers: auth(),
        body: JSON.stringify({ name: editing.name.trim(), description: editing.description || '' }),
      });
      if (r.ok) {
        toast.success('Saved');
        setEditing(null);
        await fetchLists();
      }
    } catch { toast.error('Save failed'); }
  };

  const deleteList = async (list) => {
    if (!window.confirm(`Delete "${list.name}"? This removes ${list.contact_count} contact(s).`)) return;
    try {
      const r = await fetch(`${API}/api/email/lists/${list.id}`, { method: 'DELETE', headers: auth() });
      if (r.ok) {
        toast.success('List deleted');
        if (selectedList?.id === list.id) { setSelectedList(null); setContacts([]); }
        fetchLists();
      }
    } catch { toast.error('Delete failed'); }
  };

  const uploadCSV = async (file) => {
    if (!file || !selectedList) return;
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch(`${API}/api/email/lists/${selectedList.id}/import-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
        body: fd,
      });
      const data = await r.json();
      if (r.ok) {
        setImportResult(data);
        toast.success(`Imported ${data.inserted} contact(s)`);
        fetchContacts(selectedList.id);
        fetchLists();
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch { toast.error('Import failed'); }
    setImporting(false);
    if (csvRef.current) csvRef.current.value = '';
  };

  const importPaste = async () => {
    if (!pasteText.trim() || !selectedList) return;
    setImporting(true);
    try {
      const r = await fetch(`${API}/api/email/lists/${selectedList.id}/paste`, {
        method: 'POST', headers: auth(),
        body: JSON.stringify({ text: pasteText }),
      });
      const data = await r.json();
      if (r.ok) {
        setImportResult(data);
        toast.success(`Imported ${data.inserted} contact(s)`);
        setPasteText('');
        fetchContacts(selectedList.id);
        fetchLists();
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch { toast.error('Import failed'); }
    setImporting(false);
  };

  const removeContact = async (contact) => {
    if (!selectedList) return;
    if (!window.confirm(`Remove ${contact.email}?`)) return;
    try {
      const r = await fetch(`${API}/api/email/lists/${selectedList.id}/contacts/${contact.id}`, { method: 'DELETE', headers: auth() });
      if (r.ok) {
        toast.success('Removed');
        fetchContacts(selectedList.id);
        fetchLists();
      }
    } catch { toast.error('Remove failed'); }
  };

  const downloadCSV = () => {
    if (!contacts.length) return;
    const header = ['email', 'first_name', 'last_name', 'company', 'role', 'tags'];
    const rows = contacts.map(c => header.map(k => {
      const v = k === 'tags' ? (c.tags || []).join('|') : (c[k] || '');
      return String(v).replace(/"/g, '""');
    }).map(v => `"${v}"`).join(','));
    const csv = header.join(',') + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${selectedList.name.replace(/[^a-z0-9]+/gi, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="contact-lists-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contact Lists</h1>
            <p className="text-sm text-gray-500 mt-1">Reusable recipient lists. Import once, use across many campaigns.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchLists} data-testid="refresh-lists-btn"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
            <Button onClick={() => setCreating(true)} className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="new-list-btn"><Plus className="w-4 h-4 mr-1" /> New List</Button>
          </div>
        </div>

        {creating && (
          <div className="bg-white rounded-xl border p-5 mb-4 space-y-3" data-testid="new-list-form">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Create list</h2>
              <button onClick={() => { setCreating(false); setNewName(''); setNewDesc(''); }} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
            </div>
            <div><Label className="text-xs">Name</Label><Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. WNC Sponsors 2026" data-testid="new-list-name" /></div>
            <div><Label className="text-xs">Description (optional)</Label><Input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="e.g. Confirmed sponsors + prospects" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button><Button onClick={createList} className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="create-list-submit">Create</Button></div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
          {/* Lists sidebar */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Your Lists ({lists.length})</h2>
            </div>
            {loading ? (
              <div className="p-6 text-center text-sm text-gray-400">Loading…</div>
            ) : lists.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">No lists yet. Click New List above.</div>
            ) : (
              <ul>
                {lists.map(l => (
                  <li key={l.id} className={`border-b last:border-b-0 ${selectedList?.id === l.id ? 'bg-blue-50' : ''}`}>
                    {editing?.id === l.id ? (
                      <div className="p-3 space-y-2">
                        <Input className="h-8 text-sm" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
                        <Input className="h-8 text-xs" value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} placeholder="Description" />
                        <div className="flex gap-1"><Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => setEditing(null)}>Cancel</Button><Button size="sm" className="h-7 text-xs bg-blue-900 hover:bg-blue-800 text-white flex-1" onClick={saveEdit}>Save</Button></div>
                      </div>
                    ) : (
                      <button onClick={() => setSelectedList(l)} className="w-full text-left p-3 hover:bg-gray-50 flex items-start justify-between gap-2 group" data-testid={`list-item-${l.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-800 truncate">{l.name}</div>
                          {l.description && <div className="text-xs text-gray-500 truncate">{l.description}</div>}
                          <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1"><Users className="w-3 h-3" /> {l.contact_count} contact{l.contact_count === 1 ? '' : 's'}</div>
                        </div>
                        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span onClick={(e) => { e.stopPropagation(); setEditing({ id: l.id, name: l.name, description: l.description }); }} className="text-gray-400 hover:text-blue-600"><Edit3 className="w-3.5 h-3.5" /></span>
                          <span onClick={(e) => { e.stopPropagation(); deleteList(l); }} className="text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></span>
                        </div>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Detail panel */}
          <div className="bg-white rounded-xl border overflow-hidden">
            {!selectedList ? (
              <div className="p-10 text-center text-gray-400 text-sm">Select a list on the left to view its contacts.</div>
            ) : (
              <div>
                <div className="px-5 py-4 border-b flex items-start justify-between flex-wrap gap-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-semibold text-gray-800">{selectedList.name}</h2>
                    {selectedList.description && <p className="text-xs text-gray-500 mt-0.5">{selectedList.description}</p>}
                    <p className="text-xs text-gray-400 mt-1">{totalContacts} contact{totalContacts === 1 ? '' : 's'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={downloadCSV} disabled={!contacts.length} data-testid="export-list-btn"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
                    <Button size="sm" onClick={() => setShowImport(v => !v)} className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="show-import-btn"><Upload className="w-3.5 h-3.5 mr-1" /> Import Contacts</Button>
                  </div>
                </div>

                {showImport && (
                  <div className="border-b bg-blue-50/40 p-5 space-y-4" data-testid="import-panel">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* CSV */}
                      <div className="bg-white rounded-lg border p-4">
                        <Label className="text-xs font-semibold">Upload CSV</Label>
                        <p className="text-xs text-gray-500 mb-2">Required column: <code className="bg-gray-100 px-1 rounded">email</code>. Optional: first_name, last_name, company, role, <code className="bg-gray-100 px-1 rounded">tags</code> (pipe-separated: <code className="bg-gray-100 px-1 rounded">VIP|Speaker</code>).</p>
                        <div onClick={() => csvRef.current?.click()} className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors">
                          <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                          <p className="text-xs text-gray-500">{importing ? 'Uploading…' : 'Click or drop a CSV file'}</p>
                        </div>
                        <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={e => uploadCSV(e.target.files?.[0])} data-testid="csv-file-input" />
                      </div>
                      {/* Paste */}
                      <div className="bg-white rounded-lg border p-4">
                        <Label className="text-xs font-semibold">Paste Emails</Label>
                        <p className="text-xs text-gray-500 mb-2">One per line or comma-separated. No column headers needed.</p>
                        <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} className="w-full border rounded-lg p-2 text-xs resize-none" rows={4} placeholder="alice@corp.com&#10;bob@corp.com, carol@corp.com" data-testid="paste-text" />
                        <Button size="sm" className="mt-2 bg-blue-900 hover:bg-blue-800 text-white" onClick={importPaste} disabled={!pasteText.trim() || importing} data-testid="import-paste-btn"><ClipboardPaste className="w-3.5 h-3.5 mr-1" /> Import Emails</Button>
                      </div>
                    </div>
                    {importResult && (
                      <div className="text-xs bg-white border rounded-lg p-3 space-y-1" data-testid="import-result">
                        <div className="font-semibold text-green-700">Import complete</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-gray-700">
                          <div><span className="text-gray-500">Total rows:</span> {importResult.total_rows ?? importResult.total_tokens ?? '-'}</div>
                          <div><span className="text-gray-500">Inserted:</span> <span className="text-green-700 font-medium">{importResult.inserted}</span></div>
                          <div><span className="text-gray-500">Duplicates:</span> {importResult.duplicates}</div>
                          <div><span className="text-gray-500">Invalid:</span> {importResult.invalid}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tag filter + bulk actions */}
                {(tags.length > 0 || selectedContactIds.length > 0) && (
                  <div className="border-b bg-gray-50/70 px-5 py-3 flex flex-wrap items-center gap-2" data-testid="tag-toolbar">
                    {tags.length > 0 && (
                      <>
                        <span className="text-xs text-gray-500 flex items-center gap-1 mr-1"><Tag className="w-3 h-3" /> Filter:</span>
                        {tags.map(t => {
                          const active = tagFilter.includes(t.tag);
                          return (
                            <button
                              key={t.tag}
                              onClick={() => setTagFilter(prev => active ? prev.filter(x => x !== t.tag) : [...prev, t.tag])}
                              data-testid={`tag-filter-${t.tag}`}
                              className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${active ? 'bg-blue-900 border-blue-900 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'}`}
                            >
                              {t.tag} <span className="opacity-70">({t.count})</span>
                            </button>
                          );
                        })}
                        {tagFilter.length > 0 && (
                          <button onClick={() => setTagFilter([])} className="text-xs text-gray-500 hover:text-gray-800 ml-1">Clear</button>
                        )}
                      </>
                    )}
                    {selectedContactIds.length > 0 && (
                      <div className="ml-auto flex items-center gap-2 border-l pl-3">
                        <span className="text-xs text-gray-700 font-medium">{selectedContactIds.length} selected</span>
                        <Input value={bulkTagInput} onChange={e => setBulkTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') bulkAddTags(); }} placeholder="Add tag(s), comma-separated" className="h-7 text-xs w-52" data-testid="bulk-tag-input" />
                        <Button size="sm" onClick={bulkAddTags} disabled={!bulkTagInput.trim()} className="h-7 text-xs bg-blue-900 hover:bg-blue-800 text-white" data-testid="bulk-tag-apply">Add</Button>
                        <button onClick={() => setSelectedContactIds([])} className="text-xs text-gray-500 hover:text-gray-800">Clear selection</button>
                      </div>
                    )}
                  </div>
                )}

                {/* Contact table */}
                <div className="overflow-x-auto">
                  {contacts.length === 0 ? (
                    <div className="p-10 text-center text-gray-400 text-sm">
                      No contacts yet. Click <span className="font-medium text-blue-700">Import Contacts</span> to add some.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium w-8">
                            <button onClick={toggleAllVisible} className="text-gray-400 hover:text-gray-700" title="Select all visible" data-testid="select-all-contacts">
                              {visibleContacts.length > 0 && visibleContacts.every(c => selectedContactIds.includes(c.id)) ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                            </button>
                          </th>
                          <th className="text-left px-4 py-2 font-medium">Email</th>
                          <th className="text-left px-4 py-2 font-medium">Name</th>
                          <th className="text-left px-4 py-2 font-medium">Company</th>
                          <th className="text-left px-4 py-2 font-medium">Tags</th>
                          <th className="text-right px-4 py-2 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleContacts.map((c) => {
                          const isSelected = selectedContactIds.includes(c.id);
                          return (
                            <tr key={c.id} className={`border-t hover:bg-gray-50 ${isSelected ? 'bg-blue-50/50' : ''}`} data-testid={`contact-row-${c.id}`}>
                              <td className="px-3 py-2">
                                <button onClick={() => toggleContact(c.id)} className="text-gray-400 hover:text-gray-700" data-testid={`contact-select-${c.id}`}>
                                  {isSelected ? <CheckSquare className="w-4 h-4 text-blue-700" /> : <Square className="w-4 h-4" />}
                                </button>
                              </td>
                              <td className="px-4 py-2 text-gray-800">{c.email}</td>
                              <td className="px-4 py-2 text-gray-700">{[c.first_name, c.last_name].filter(Boolean).join(' ') || <span className="text-gray-300">—</span>}</td>
                              <td className="px-4 py-2 text-gray-700">{c.company || <span className="text-gray-300">—</span>}</td>
                              <td className="px-4 py-2">
                                <div className="flex flex-wrap gap-1 items-center" data-testid={`contact-tags-${c.id}`}>
                                  {(c.tags || []).map(t => (
                                    <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800 border border-blue-200">
                                      {t}
                                      <button onClick={() => removeTagFromContact(c, t)} className="hover:text-red-600" data-testid={`remove-tag-${c.id}-${t}`}><X className="w-3 h-3" /></button>
                                    </span>
                                  ))}
                                  {editingTagFor === c.id ? (
                                    <input
                                      autoFocus
                                      value={tagInput}
                                      onChange={e => setTagInput(e.target.value)}
                                      onBlur={() => { if (tagInput.trim()) addTagToContact(c, tagInput); else { setEditingTagFor(null); setTagInput(''); } }}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') { e.preventDefault(); addTagToContact(c, tagInput); }
                                        if (e.key === 'Escape') { setEditingTagFor(null); setTagInput(''); }
                                      }}
                                      placeholder="tag name"
                                      className="border rounded px-1.5 py-0.5 text-xs w-24 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                      data-testid={`tag-input-${c.id}`}
                                    />
                                  ) : (
                                    <button onClick={() => { setEditingTagFor(c.id); setTagInput(''); }} className="text-gray-400 hover:text-blue-700 px-1.5 py-0.5 rounded-full border border-dashed border-gray-300 hover:border-blue-400 text-xs" data-testid={`add-tag-${c.id}`}>
                                      + tag
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-right"><button onClick={() => removeContact(c)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {(tagFilter.length > 0 && visibleContacts.length === 0 && contacts.length > 0) && (
                    <div className="p-6 text-center text-sm text-gray-500">No contacts match the selected tag filter.</div>
                  )}
                  {totalContacts > contacts.length && (
                    <div className="px-4 py-2 text-xs text-gray-500 border-t bg-gray-50">Showing first {contacts.length} of {totalContacts}. Full list is used at import time.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
